export type BrandSearchSource = {
  title: string;
  excerpt: string;
  href: string;
};

export type BrandKeywordResult = BrandSearchSource & {
  score: number;
};

function normalize(value: string): string {
  return value
    .normalize('NFKC')
    .toLocaleLowerCase()
    .replace(/[\s\p{P}\p{S}]+/gu, '');
}

function queryTerms(query: string): string[] {
  const runs = query.normalize('NFKC').toLocaleLowerCase().match(/[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]+|[a-z0-9]+/gu) || [];
  const terms = new Set<string>();

  for (const run of runs) {
    if (/^[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]+$/u.test(run)) {
      if (run.length === 1) terms.add(run);
      terms.add(run);
      for (let index = 0; index < run.length - 1; index += 1) {
        terms.add(run.slice(index, index + 2));
      }
    } else if (run.length > 1) {
      terms.add(run);
    }
  }

  return [...terms];
}

function makeExcerpt(source: BrandSearchSource, terms: string[]): string {
  const excerpt = source.excerpt.replace(/\s+/g, ' ').trim();
  if (excerpt.length <= 180) return excerpt;

  const lowerExcerpt = excerpt.toLocaleLowerCase();
  const matchPositions = terms
    .map((term) => lowerExcerpt.indexOf(term))
    .filter((position) => position >= 0);
  if (matchPositions.length === 0) return `${excerpt.slice(0, 177)}…`;

  const start = Math.max(0, Math.min(...matchPositions) - 60);
  const end = Math.min(excerpt.length, start + 180);
  return `${start > 0 ? '…' : ''}${excerpt.slice(start, end)}${end < excerpt.length ? '…' : ''}`;
}

export function searchBrandSources(query: string, sources: BrandSearchSource[]): BrandKeywordResult[] {
  const normalizedQuery = normalize(query);
  const terms = queryTerms(query);
  if (!normalizedQuery || terms.length === 0) return [];

  return sources
    .map((source) => {
      const title = normalize(source.title);
      const excerpt = normalize(source.excerpt);
      const matchedTerms = terms.filter((term) => title.includes(term) || excerpt.includes(term));
      const minimumMatches = Math.max(1, Math.ceil(terms.length * 0.25));
      if (matchedTerms.length < minimumMatches) return null;

      const titleMatches = terms.filter((term) => title.includes(term)).length;
      const score =
        (title.includes(normalizedQuery) ? 24 : 0) +
        (excerpt.includes(normalizedQuery) ? 12 : 0) +
        titleMatches * 4 +
        matchedTerms.length;
      return { ...source, excerpt: makeExcerpt(source, terms), score };
    })
    .filter((result): result is BrandKeywordResult => result !== null)
    .sort((left, right) => right.score - left.score)
    .slice(0, 6);
}
